import { Link, useRouterState } from "@tanstack/react-router";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard,
  ListChecks,
  Search,
  DollarSign,
  MessageSquare,
  CalendarCheck,
  Heart,
  PlusSquare,
  User as UserIcon,
  Stethoscope,
  CalendarDays,
  CreditCard,
  BarChart3,
} from "lucide-react";


type Item = { to: string; label: string; icon: React.ComponentType<{ className?: string }>; exact?: boolean; tab?: string };
type Group = { label: string; items: Item[] };

const ADMIN_GROUPS: Group[] = [
  {
    label: "Admin",
    items: [
      { to: "/dashboard/admin", label: "Overview", icon: LayoutDashboard, exact: true },
      { to: "/dashboard/admin/listings", label: "Listings", icon: ListChecks },
      { to: "/dashboard/admin/seo", label: "SEO", icon: Search },
      { to: "/dashboard/admin/revenue", label: "Revenue", icon: DollarSign },
      { to: "/dashboard/admin/comms", label: "Communication", icon: MessageSquare },
    ],

  },
];

// Match map: a sidebar item is "active" when current path belongs to any of these section prefixes.
const ADMIN_SECTION_MATCH: Record<string, string[]> = {
  "/dashboard/admin": [],
  "/dashboard/admin/listings": [
    "/dashboard/admin/listings",
    "/dashboard/admin/claims",
    "/dashboard/admin/requests",
    "/dashboard/admin/avatars",
    "/dashboard/admin/reviews",
    "/dashboard/admin/bulk-imports",
    "/dashboard/admin/data-quality",
    "/dashboard/admin/add-listings",
  ],
  "/dashboard/admin/seo": [
    "/dashboard/admin/seo",
    "/dashboard/admin/seo-search-console",
    "/dashboard/admin/seo-indexnow",
    "/dashboard/admin/seo-programmatic",
    "/dashboard/admin/seo-press",
    "/dashboard/admin/gsc-monitor",
    "/dashboard/admin/sitemap-health",
    "/dashboard/admin/seo-competitor",
    "/dashboard/admin/traffic",
    "/dashboard/admin/blog",
  ],
  "/dashboard/admin/revenue": [
    "/dashboard/admin/revenue",
    "/dashboard/admin/revenue-subscriptions",
    "/dashboard/admin/revenue-monthly",
    "/dashboard/admin/revenue-yearly",
    "/dashboard/admin/revenue-featured",
    "/dashboard/admin/revenue-cancellations",
    "/dashboard/admin/lifetime",
  ],
  "/dashboard/admin/comms": [
    "/dashboard/admin/comms",
    "/dashboard/admin/comms-email",
    "/dashboard/admin/call",
    "/dashboard/admin/sms",
    "/dashboard/admin/whatsapp",
    "/dashboard/admin/notifications",
    "/dashboard/admin/email-logs",
    "/dashboard/admin/email-verification",
    "/dashboard/admin/ai",
  ],
};


const DOCTOR_GROUPS: Group[] = [
  {
    label: "Provider",
    items: [
      { to: "/dashboard/doctor", label: "Overview", icon: BarChart3, exact: true },
      { to: "/dashboard/doctor", label: "My listings", icon: Stethoscope, tab: "listings" },
      { to: "/dashboard/doctor", label: "My calendar", icon: CalendarDays, tab: "calendar" },
      { to: "/dashboard/doctor", label: "My bookings", icon: CalendarCheck, tab: "bookings" },
      { to: "/dashboard/doctor", label: "Billing", icon: CreditCard, tab: "billing" },
    ],
  },
];

const PATIENT_GROUPS: Group[] = [
  {
    label: "You",
    items: [
      { to: "/dashboard/patient", label: "Dashboard", icon: LayoutDashboard, exact: true },
      { to: "/dashboard/patient", label: "Appointments", icon: CalendarCheck },
      { to: "/dashboard/patient", label: "Saved", icon: Heart },
    ],
  },
];

function pickGroups(role: string | null): Group[] {
  if (role === "admin") return ADMIN_GROUPS;
  if (role === "doctor") return DOCTOR_GROUPS;
  return PATIENT_GROUPS;
}

// Strictly derive sidebar surface from the current URL section so that admin
// and doctor dashboards NEVER render each other's navigation. The user's
// auth role is only used as a fallback on the bare /dashboard index.
function roleFromPath(path: string, fallback: string | null): string | null {
  if (path.startsWith("/dashboard/admin")) return "admin";
  if (path.startsWith("/dashboard/doctor")) return "doctor";
  if (path.startsWith("/dashboard/patient")) return "patient";
  if (path.startsWith("/dashboard/new-listing")) return "doctor";
  return fallback;
}

export function DashboardSidebar() {
  const { primaryRole, user } = useAuth();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const path = useRouterState({ select: (s) => s.location.pathname });
  const currentTab = useRouterState({ select: (s) => (s.location.search as any)?.tab as string | undefined });
  const sectionRole = roleFromPath(path, primaryRole ?? null);
  const groups = pickGroups(sectionRole);


  const isActive = (item: Item) => {
    // Doctor dashboard items differentiate by `tab` search param.
    if (item.to === "/dashboard/doctor") {
      if (item.exact) return path === "/dashboard/doctor" && !currentTab;
      if (item.tab) return path === "/dashboard/doctor" && currentTab === item.tab;
    }
    if (item.exact) return path === item.to;
    const prefixes = ADMIN_SECTION_MATCH[item.to];
    if (prefixes && prefixes.length > 0) {
      return prefixes.some((p) => path === p || path.startsWith(p + "/"));
    }
    return path === item.to || path.startsWith(item.to + "/");
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-sidebar-border top-14 !h-[calc(100svh-3.5rem)]"
    >
      <SidebarContent className="px-0 py-2 gap-0 bg-background">
        {groups.map((group, gi) => (
          <SidebarGroup
            key={group.label}
            className={[
              "py-1.5",
              gi > 0 ? "border-t border-border/60" : "",
            ].join(" ")}
          >
            {!collapsed && (
              <SidebarGroupLabel className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground/70 px-4 h-6">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu className="gap-0 px-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  const linkSearch = item.to === "/dashboard/doctor"
                    ? ({ tab: item.tab, billing: undefined, pid: undefined } as any)
                    : undefined;
                  return (
                    <SidebarMenuItem key={item.to + item.label}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                        className={[
                          "h-11 rounded-md text-[14px] gap-3 px-2.5 transition-colors relative",
                          "text-foreground/70 hover:text-foreground hover:bg-muted/60 font-medium",
                          "data-[active=true]:bg-primary/10 data-[active=true]:text-foreground data-[active=true]:font-semibold",
                          "data-[active=true]:hover:bg-primary/15",
                        ].join(" ")}
                      >
                        <Link to={item.to as any} search={linkSearch} className="flex items-center gap-3">
                          {active && !collapsed && (
                            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-primary" aria-hidden />
                          )}
                          <span
                            className={[
                              "flex items-center justify-center h-7 w-7 rounded-md shrink-0 transition-colors",
                              active
                                ? "bg-primary/15 text-primary"
                                : "bg-muted/70 text-muted-foreground",
                            ].join(" ")}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-3 bg-background">
        <div className="flex items-center gap-2 px-1 py-0.5 text-[12px] text-muted-foreground truncate">
          <UserIcon className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && <span className="truncate">{user?.email ?? "Signed in"}</span>}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
